import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { ChatsCircle, PaperPlaneTilt, X } from "@phosphor-icons/react";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

// Item 6 (Neo Iteration 134) — dedicated in-portal messaging, separate from the WhatsApp/Nia-chat
// relay systems. Rendered unconditionally in DashboardLayout (+ AdminHomePage) so it's visible on
// every admin/manager/rep page without navigating anywhere.
const BASE_BY_ROLE = { admin: "/admin", manager: "/manager", affiliate: "/rep" };

export function MessagingPanel() {
  const { user } = useAuth();
  const role = user?.role;
  const base = BASE_BY_ROLE[role];
  const canCompose = role === "admin" || role === "manager";
  const [open, setOpen] = useState(false);
  const [composing, setComposing] = useState(false);
  const [inbox, setInbox] = useState({ received: [], sent: [], unread_count: 0 });
  const [recipients, setRecipients] = useState([]);
  const [targetType, setTargetType] = useState("rep");
  const [targetId, setTargetId] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [digestLastSent, setDigestLastSent] = useState(null);
  const ref = useRef(null);

  const fetchInbox = () => {
    if (!base) return;
    api.get(`${base}/messages/inbox`).then(({ data }) => setInbox(data)).catch(() => {});
  };

  useEffect(() => {
    if (!base) return;
    fetchInbox();
    const interval = setInterval(fetchInbox, 20000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  useEffect(() => {
    // Vince's "Last digest sent" readout (Auto-Assign Daily Digest) — manager-only.
    if (role !== "manager") return;
    api.get("/manager/messages/digest-status").then(({ data }) => setDigestLastSent(data.last_sent)).catch(() => {});
  }, [role]);

  useEffect(() => {
    if (!canCompose || !open) return;
    const url = role === "admin" ? "/admin/users" : "/manager/reps/cards";
    api.get(url).then(({ data }) => {
      const list = role === "admin"
        ? data.filter((u) => u.role === "affiliate" && u.account_status !== "revoked" && !u.is_test_account)
        : data;
      setRecipients(list.map((r) => ({ id: r.id, display_name: r.display_name, user_id: r.user_id })));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCompose, open, role]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!base) return null;

  const markRead = async (id, read) => {
    if (read) return;
    try { await api.patch(`${base}/messages/${id}/read`); fetchInbox(); } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try { await api.patch(`${base}/messages/read-all`); fetchInbox(); } catch { /* ignore */ }
  };

  const send = async () => {
    if (!text.trim()) { toast.error("Type a message first"); return; }
    if (targetType === "rep" && !targetId) { toast.error("Choose a rep"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`${base}/messages/send`, {
        message: text.trim(), target_type: targetType, target_id: targetType === "rep" ? targetId : undefined,
      });
      toast.success(data.message);
      setText("");
      setTargetId("");
      setComposing(false);
      fetchInbox();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="messaging-panel-button"
        onClick={() => setOpen((v) => !v)}
        title="Direct messages between you, your manager/admin, and reps — separate from notifications."
        className="relative p-2 text-igc-cream hover:text-igc-goldText transition-colors"
      >
        <ChatsCircle size={22} weight={inbox.unread_count > 0 ? "fill" : "duotone"} />
        {inbox.unread_count > 0 && (
          <span data-testid="messaging-panel-unread-count" className="absolute -top-1 -right-1 bg-igc-gold text-black text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-medium">
            {inbox.unread_count > 9 ? "9+" : inbox.unread_count}
          </span>
        )}
      </button>
      {open && createPortal(
        <div data-testid="messaging-panel-slideover" className="fixed top-0 right-0 h-full w-full sm:w-96 bg-igc-surface border-l border-igc-gold/20 shadow-2xl z-[9999] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-igc-gold/10">
            <p className="font-serif text-igc-cream text-lg">Messages</p>
            <button data-testid="messaging-panel-close-button" onClick={() => setOpen(false)} className="text-igc-muted hover:text-igc-cream">
              <X size={18} weight="bold" />
            </button>
          </div>
          {role === "manager" && (
            <p data-testid="messaging-panel-digest-last-sent" className="px-4 py-2 text-igc-muted text-xs border-b border-igc-gold/10" title="The Auto-Assign Daily Digest is silent on days with zero HIGH-tier assignments, so this only updates when one actually goes out.">
              Last Auto-Assign digest sent: {digestLastSent ? formatDistanceToNow(new Date(digestLastSent), { addSuffix: true }) : "not yet sent"}
            </p>
          )}

          {canCompose && (
            <div className="p-4 border-b border-igc-gold/10">
              {!composing ? (
                <Button data-testid="messaging-panel-compose-open-button" onClick={() => setComposing(true)} className="w-full bg-igc-gold hover:bg-igc-goldHover text-black flex items-center justify-center gap-2">
                  <PaperPlaneTilt size={14} weight="bold" /> New Message
                </Button>
              ) : (
                <div className="space-y-2">
                  <Select value={targetType} onValueChange={(v) => { setTargetType(v); setTargetId(""); }}>
                    <SelectTrigger data-testid="messaging-panel-target-type-select" className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                      <SelectItem value="rep">One Rep</SelectItem>
                      <SelectItem value="all_reps">{role === "admin" ? "All Reps (Broadcast)" : "All My Reps (Broadcast)"}</SelectItem>
                      {role === "manager" && <SelectItem value="admin">Message Wes (Admin)</SelectItem>}
                    </SelectContent>
                  </Select>
                  {targetType === "rep" && (
                    <Select value={targetId} onValueChange={setTargetId}>
                      <SelectTrigger data-testid="messaging-panel-recipient-select" className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue placeholder="Choose a rep..." /></SelectTrigger>
                      <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream max-h-60">
                        {recipients.map((r) => <SelectItem key={r.id} value={r.id}>{r.display_name} ({r.user_id})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Textarea
                    data-testid="messaging-panel-textarea"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Type your message..."
                    className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream min-h-24"
                  />
                  <div className="flex gap-2">
                    <Button data-testid="messaging-panel-send-button" onClick={send} disabled={sending} className="flex-1 bg-igc-gold hover:bg-igc-goldHover text-black">
                      {sending ? "Sending…" : "Send"}
                    </Button>
                    <Button data-testid="messaging-panel-cancel-compose-button" onClick={() => { setComposing(false); setText(""); }} variant="outline" className="border-igc-gold/30 text-igc-goldText">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-2 border-b border-igc-gold/10">
              <p className="text-igc-muted text-xs uppercase tracking-widest">Inbox</p>
              {inbox.unread_count > 0 && (
                <button data-testid="messaging-panel-mark-all-read" onClick={markAllRead} className="text-igc-goldText text-xs hover:underline">Mark all read</button>
              )}
            </div>
            {inbox.received.length === 0 && <p className="p-6 text-igc-muted text-sm text-center" data-testid="messaging-panel-inbox-empty">No messages yet.</p>}
            {inbox.received.map((m) => (
              <button
                key={m.id}
                data-testid={`messaging-panel-message-${m.id}`}
                onClick={() => markRead(m.id, m.read)}
                className={`w-full text-left p-4 border-b border-igc-gold/5 border-l-2 ${m.read ? "border-l-transparent" : "border-l-igc-gold"}`}
              >
                <p className="text-igc-goldText text-xs mb-1">From {m.from_name}</p>
                <p className="text-igc-cream text-sm">{m.message}</p>
                <p className="text-igc-muted text-xs mt-1">{formatDistanceToNow(new Date(m.timestamp), { addSuffix: true })}</p>
              </button>
            ))}
            {canCompose && inbox.sent.length > 0 && (
              <>
                <p className="text-igc-muted text-xs uppercase tracking-widest px-4 py-2 border-b border-t border-igc-gold/10 mt-2">Sent</p>
                {inbox.sent.map((m) => (
                  <div key={m.id} data-testid={`messaging-panel-sent-${m.id}`} className="p-4 border-b border-igc-gold/5">
                    <p className="text-igc-muted text-xs mb-1">To {m.to_name}</p>
                    <p className="text-igc-cream text-sm">{m.message}</p>
                    <p className="text-igc-muted text-xs mt-1">{formatDistanceToNow(new Date(m.timestamp), { addSuffix: true })}</p>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
